// Stock counts in Inventory (inventory prototype; gap #15): count the warehouse or a truck and post
// it (stockCounts.post). The count is blind (F-60): what the books expect stays hidden while counting.
// "Done counting" locks the counts and shows the differences, as the prototype does; each one needs a
// reason, then the count is posted. A difference the server still finds unexplained is marked.

import { useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { Empty, Loading, Money, Panel, errorText, useQuery } from '../books/ui.tsx';
import { newId } from '../shared/ids.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { qtyText, type Level, type StockLocation } from './model.ts';
import {
  COUNT_REASONS,
  countDiffs,
  countLines,
  countProblems,
  diffText,
  linesNeedingReason,
  reasonsMissing,
  type CountForm,
} from './orders-model.ts';
import { RenameLocation } from './RenameLocation.tsx';
import './inventory.css';

interface Posted {
  lines: { itemId: string; expected: string; counted: string; difference: string; valueCents?: number }[];
  valueCents?: number;
}

export function Counts({
  locations,
  toast,
  mayCount,
  onRenamed,
}: {
  locations: StockLocation[];
  toast: (m: string) => void;
  mayCount: boolean;
  /** A location was renamed here: the list re-reads. */
  onRenamed: () => void;
}) {
  const [locationId, setLocationId] = useState(locations[0]?.id ?? '');
  const [round, setRound] = useState(0); // a new count after posting one
  // every truck and the warehouse are listed here, stocked or not: renaming one starts here too
  const [renaming, setRenaming] = useState<StockLocation | null>(null);
  const here = locations.find((l) => l.id === locationId);
  return (
    <Panel
      title="Count"
      aside={
        <div className="row-actions">
          <label className="inline-label">
            <span className="flabel">Where</span>
            <select
              className="field"
              value={locationId}
              onChange={(e) => {
                setLocationId(e.target.value);
                setRound((r) => r + 1);
              }}
            >
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
          {mayCount && here && (
            <button type="button" className="btn quiet" onClick={() => setRenaming(here)}>
              Rename
            </button>
          )}
        </div>
      }
    >
      {renaming && (
        <RenameLocation
          location={renaming}
          onClose={() => setRenaming(null)}
          onRenamed={(name) => {
            toast(`${renaming.name} is now ${name}.`);
            setRenaming(null);
            onRenamed();
          }}
        />
      )}
      {!mayCount ? (
        <Empty>Counts are posted by the warehouse, the owner or a manager.</Empty>
      ) : locationId ? (
        <CountSheet
          key={`${locationId}:${round}`}
          locationId={locationId}
          name={locations.find((l) => l.id === locationId)?.name ?? ''}
          toast={toast}
          onAgain={() => setRound((r) => r + 1)}
        />
      ) : (
        <Empty>No warehouse or trucks yet.</Empty>
      )}
    </Panel>
  );
}

function CountSheet({
  locationId,
  name,
  toast,
  onAgain,
}: {
  locationId: string;
  name: string;
  toast: (m: string) => void;
  onAgain: () => void;
}) {
  const levels = useQuery<{ levels: Level[] }>('stock.levels', { locationId });
  const [lines, setLines] = useState<CountForm[] | null>(null);
  if (levels.data && lines === null)
    setLines(
      levels.data.levels.map((l) => ({
        itemId: l.itemId,
        name: l.itemName,
        counted: '',
        reason: '',
        note: '',
      })),
    );
  const flagged = new Set((levels.data?.levels ?? []).filter((l) => l.countNeeded).map((l) => l.itemId));
  // Counting is blind; once done, the counts lock and the books' figures show (prototype).
  const [done, setDone] = useState(false);
  const [needReason, setNeedReason] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posted, setPosted] = useState<Posted | null>(null);
  const [countId] = useState(newId);
  const [tried, setTried] = useState(false);
  // a press of Post with a reason missing: only then are the lines marked
  const [postTried, setPostTried] = useState(false);
  if (!levels.data || !lines)
    return (
      <Loading
        error={levels.error}
        what={`what is stocked at ${name}`}
        kind="table"
        cols={['Item', 'Counted']}
        retry={() => void levels.reload()}
      />
    );
  if (!lines.length) return <Empty>Nothing is stocked at {name} yet, so there is nothing to count.</Empty>;
  const problems = countProblems(lines);
  const diffs = countDiffs(lines, Object.fromEntries(levels.data.levels.map((l) => [l.itemId, l.onHand])));
  const missing = done ? reasonsMissing(lines, diffs) : [];
  const off = lines.filter((l) => diffs[l.itemId]!.diff !== 0).length;
  const set = (i: number, patch: Partial<CountForm>) =>
    setLines((ls) => ls!.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const finish = () => {
    setTried(true);
    if (!problems.length) setDone(true);
  };
  const post = async () => {
    if (missing.length) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<Posted>('stockCounts.post', {
        countId,
        locationId,
        countedAt: new Date().toISOString(),
        lines: countLines(lines),
      });
      setPosted(r);
      const n = r.lines.filter((l) => Number(l.difference) !== 0).length;
      toast(
        n
          ? `Count posted: ${n} ${n === 1 ? 'difference' : 'differences'} adjusted.`
          : 'Count posted: it matched the books.',
      );
    } catch (e) {
      // The books moved since they were read: the server names the line still unexplained.
      if (e instanceof RpcError) setNeedReason(linesNeedingReason(e.detail, lines));
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const nameOf = (id: string) => lines.find((l) => l.itemId === id)?.name ?? '';

  if (posted)
    return (
      <div className="count-done">
        <p className="notice ok" role="status">
          Posted for {name}. The count flags here are cleared.
        </p>
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table" aria-label="What the count found">
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">The books said</th>
                <th className="num">Counted</th>
                <th className="num">Difference</th>
                {posted.valueCents !== undefined && <th className="num">Value</th>}
              </tr>
            </thead>
            <tbody>
              {posted.lines.map((l) => (
                <tr key={l.itemId}>
                  <td>{nameOf(l.itemId)}</td>
                  <td className="num">{qtyText(l.expected)}</td>
                  <td className="num">{qtyText(l.counted)}</td>
                  <td className={`num${Number(l.difference) !== 0 ? ' low' : ''}`}>
                    {diffText(l.difference)}
                  </td>
                  {posted.valueCents !== undefined && (
                    <td className="num">
                      {l.valueCents !== undefined ? <Money cents={l.valueCents} /> : '–'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="row-actions">
          <button type="button" className="btn" onClick={onAgain}>
            Count again
          </button>
        </div>
      </div>
    );

  return (
    <form
      className="count-sheet"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        if (done) {
          setPostTried(true);
          void post();
        } else finish();
      }}
    >
      {done ? (
        <p className={`notice${off ? ' warn' : ' ok'}`} role="status">
          {off
            ? `Counts locked. ${off} ${off === 1 ? 'line is' : 'lines are'} off from the books: say why on each, then post.`
            : 'Counts locked. Everything matches the books.'}
        </p>
      ) : (
        <p className="muted small">
          A blind count: the books’ figures stay hidden until you’re done, so the count is your own.
        </p>
      )}
      <ul className="count-lines">
        {lines.map((l, i) => {
          const d = diffs[l.itemId]!;
          const isOff = done && d.diff !== 0;
          const mark = needReason.includes(l.itemId) || (postTried && missing.includes(l.itemId));
          return (
            <li
              key={l.itemId}
              className={`${mark ? 'need' : ''}${done ? ' locked' : ''}`.trim() || undefined}
            >
              <div className="count-name">
                <b>{l.name}</b>
                {flagged.has(l.itemId) && <span className="chip warn">Count asked for</span>}
                {mark && <span className="chip bad">Needs a reason</span>}
              </div>
              {done ? (
                <div className="count-figs">
                  <span>
                    Counted <b>{qtyText(l.counted)}</b>
                  </span>
                  <span className="muted small">books {qtyText(d.expected)}</span>
                  <span className={isOff ? 'off' : 'muted small'}>
                    {isOff ? diffText(String(d.diff)) : 'matches'}
                  </span>
                </div>
              ) : (
                <label>
                  <span className="flabel">Counted</span>
                  <input
                    className="field"
                    inputMode="decimal"
                    value={l.counted}
                    onChange={(e) => set(i, { counted: e.target.value })}
                  />
                </label>
              )}
              {isOff && (
                <label>
                  <span className="flabel">Why it’s off</span>
                  <select
                    className="field"
                    value={l.reason}
                    onChange={(e) => set(i, { reason: e.target.value })}
                  >
                    <option value="">Pick a reason…</option>
                    {COUNT_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {isOff && l.reason && (
                <label>
                  <span className="flabel">Note (optional)</span>
                  <input
                    className="field"
                    maxLength={300}
                    value={l.note}
                    onChange={(e) => set(i, { note: e.target.value })}
                  />
                </label>
              )}
            </li>
          );
        })}
      </ul>
      {tried && !done && problems.length > 0 && (
        <div className="notice warn" role="alert">
          <ul>
            {problems.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="row-actions">
        {done ? (
          <SaveButton
            busy={busy}
            busyLabel="Posting…"
            error={error ?? (postTried && missing.length ? 'Give a reason for every difference.' : null)}
            onClick={() => {
              setPostTried(true);
              void post();
            }}
          >
            Post the count
          </SaveButton>
        ) : (
          <button type="button" className="btn primary" onClick={finish}>
            Done counting · show differences
          </button>
        )}
      </div>
    </form>
  );
}
